import React, { useState, useEffect, useCallback } from "react";
import { API_ENDPOINT } from "../../utils/constants";
import useGetStaffList from "../../hooks/useGetStaffList";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Pagination,
  CircularProgress,
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  MenuItem,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from "@mui/icons-material/Search";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, getHeader } from "../../utils/util";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import BackBtn from "../../widgets/BackBtn";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import AddIcon from "@mui/icons-material/Add";
import useGetDesignations from "../../hooks/useGetDesignations";

// Simple debounce function
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const StaffList = ({ type }) => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchQ, setSearchQ] = useState("");
  const [debouncedSearchQ, setDebouncedSearchQ] = useState("");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const limitNumber = 30;

  const { data, isLoading, isError, error } = useGetStaffList(
    type,
    pageNumber,
    limitNumber,
    debouncedSearchQ
  );
  const { data: comS } = useGetDesignations();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [inviteError, setInviteError] = useState(null);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (!isLoading && !isError && data) {
      setHasLoadedOnce(true);
    }
  }, [isLoading, isError, data]);

  const debouncedSetSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchQ(value);
      setPageNumber(1);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQ(value);
    debouncedSetSearch(value);
  };

  const handleOpen = () => {
    setOpen(true);
    setEmail("");
    setInviteError(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInvite = async () => {
    try {
      setInviting(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}admin/invite-member`,
        { email, type, designation },
        getHeader()
      );
      toast.success(data.message);
      handleClose();
    } catch (err) {
      setInviteError(getErrorResponse(err));
    } finally {
      setInviting(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPageNumber(value);
  };

  const handleRowClick = (staff) => {
    // For now, just log the staff details. Replace with your navigation/view logic
    //console.log("View details for:", staff);
    navigate(`/home/staff/${staff._id}`);
    // Example: navigate(`/staff/${staff._id}`) if using React Router
  };

  if (!hasLoadedOnce && isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: "1200px", mx: "auto" }}>
      <BackBtn />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            mb: { xs: 1, sm: 0 },
          }}
        >
          <GroupIcon
            sx={{ mr: 1, fontSize: { xs: "1.5rem", sm: "1.75rem" } }}
          />
          {type} List
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <TextField
            size="small"
            placeholder="Search staff..."
            value={searchQ}
            onChange={handleSearchChange}
            fullWidth={true}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "action" }} />,
              endAdornment: isLoading ? (
                <CircularProgress size={20} sx={{ ml: 1 }} />
              ) : null,
            }}
            sx={{ flexGrow: 1 }}
            inputProps={{ style: { fontSize: "0.9rem" } }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleOpen}
            startIcon={<PersonAddIcon />}
            sx={{
              flexShrink: 0,
              whiteSpace: "nowrap",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            Invite
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              navigate(`/home/add-staff-manually/${type}`);
            }}
            startIcon={<AddIcon />}
            sx={{
              flexShrink: 0,
              whiteSpace: "nowrap",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            Add
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              navigate(`/home/import-staff-excel/${type}`);
            }}
            startIcon={<ImportExportIcon />}
            sx={{
              flexShrink: 0,
              whiteSpace: "nowrap",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            Import
          </Button>
        </Box>
      </Box>
      {data?.items?.length > 0 ? (
        <Box>
          <Grid container spacing={2}>
            {data.items.map((staff) => (
              <Grid item xs={12} sm={6} md={4} key={staff._id}>
                <Card
                  sx={{
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardActionArea onClick={() => handleRowClick(staff)}>
                    <CardContent
                      sx={{ display: "flex", alignItems: "center", p: 2 }}
                    >
                      <Avatar
                        src={staff.profileImage}
                        alt={staff.name}
                        sx={{
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          mr: 2,
                          border: "2px solid #eee",
                        }}
                      />
                      <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "medium",
                            fontSize: { xs: "1rem", sm: "1.125rem" },
                            mb: 0.5,
                          }}
                          noWrap
                        >
                          {staff.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "0.9rem" },
                            mb: 0.5,
                          }}
                          noWrap
                        >
                          {staff.email}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "0.9rem" },
                            fontWeight: "medium",
                          }}
                        >
                          {staff.designation}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
          {/* Pagination */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Pagination
              count={data?.pagination?.totalPages || 1}
              page={pageNumber}
              onChange={handlePageChange}
              color="primary"
              disabled={isLoading}
              size="medium"
              sx={{
                ".MuiPagination-ul": {
                  flexWrap: "wrap",
                  justifyContent: "center",
                },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textAlign: "center",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              Showing {(pageNumber - 1) * limitNumber + 1} -{" "}
              {Math.min(pageNumber * limitNumber, data?.pagination?.totalUsers)}{" "}
              of {data?.pagination?.totalUsers} staff members
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            textAlign: "center",
            fontSize: { xs: "1rem", sm: "1.125rem" },
            py: 2,
          }}
        >
          No staff members found
        </Typography>
      )}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            fontWeight: "bold",
          }}
        >
          <PersonAddIcon
            sx={{ mr: 1, fontSize: { xs: "1.5rem", sm: "1.75rem" } }}
          />
          Invite New Staff Member
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!inviteError}
            helperText={inviteError || "Enter the email address to invite"}
            InputProps={{
              startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ mt: 1 }}
            inputProps={{ style: { fontSize: "0.9rem" } }}
            InputLabelProps={{ style: { fontSize: "0.9rem" } }}
          />
          <TextField
            fullWidth
            select
            label="Designation"
            variant="outlined"
            sx={{ mt: 2 }}
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
          >
            {comS?.map((option) => (
              <MenuItem key={option._id} value={option.designation}>
                {option.designation}
              </MenuItem>
            ))}
          </TextField>
          <Box mt={1} display={"flex"} justifyContent={"end"}>
            If not in the list &nbsp;
            <Link to={"/home/settings"}>add in settings</Link>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, p: { xs: 1, sm: 2 } }}>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            variant="outlined"
            color="error"
            fullWidth={false}
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleInvite}
            variant="contained"
            loading={inviting}
            startIcon={<SendIcon />}
            disabled={!email || !/^\S+@\S+\.\S+$/.test(email)}
            fullWidth={false}
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            Send Invite
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffList;
